// The classification runner (plan v2 §4.2): a live classification workflow walks one new lead from its trigger to
// its end. It reads the registry's fields once (counted on the reg-fields API), and keeps only the result: the tags
// it adds and the batches it puts the person in. The trace names the rule that matched, never a field value.
import {
  ClassifyField,
  KIND_NODES,
  type NodeType,
  type TraceStep,
  type WorkflowDocument,
} from '../../schemas'
import { CLASSIFY_FIELD_LABEL } from '@/constants/classification'
import { unitFor } from '../random'
import type { ClassifyRecord, LeadRecord, State, VersionRecord } from '../state'
import { API_ID, apiSwitchedOff, countCampaignCall } from './api-traffic'
import { addMember } from './batches'
import { newId } from './ids'
import { latestList, pinInList } from './lists'
import { fieldsOf, fieldValue, type LeadFields } from './registry-fields'
import { nowIso } from './sending-window'
import { liveVersion } from './workflow-versions'
import { stateOf } from './workflow-state'

const MAX_STEPS = 40
const RUNS_KEPT = 300
const TRIGGER = KIND_NODES.classification[0]

interface Walk {
  state: State
  lead: LeadRecord
  fields: LeadFields
  cfg: Record<string, unknown>
  out: ClassifyRecord
}

const text = (walk: Walk, key: string, fallback = '') => {
  const value = walk.cfg[key]
  return value === undefined || value === null || value === '' ? fallback : String(value)
}
const split = (value: string) =>
  value
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean)

function fieldCheck(walk: Walk): string {
  const field = ClassifyField.catch('city').parse(walk.cfg.field)
  const values = split(text(walk, 'values'))
  const yes =
    field === 'tag'
      ? values.some((tag) => walk.lead.tags.includes(tag))
      : values.includes(fieldValue(walk.fields, walk.lead, field))
  note(walk, `${CLASSIFY_FIELD_LABEL[field]} is one of ${values.join(' | ') || '—'} → ${yes ? 'yes' : 'no'}`)
  return yes ? 'yes' : 'no'
}

function listCheck(walk: Walk): string {
  const list = latestList(walk.state, text(walk, 'list'))
  const yes = !!list && pinInList(list, walk.fields.pin)
  note(
    walk,
    list ? `PIN code in "${list.name}" v${list.version} → ${yes ? 'yes' : 'no'}` : 'No list chosen → no'
  )
  return yes ? 'yes' : 'no'
}

/** Whether a score rule's fact holds: a tag the person has, came back within 7 days, or clicked one of our links. */
function factHolds(walk: Walk, fact: string): boolean {
  if (fact === 'returned_7d') return unitFor(walk.state, `${walk.lead.id}:returned`) < 0.15
  if (fact === 'clicked_link')
    return walk.state.messages.some((m) => m.lead_code === walk.lead.lead_code && m.outcome === 'clicked')
  return walk.lead.tags.includes(fact)
}

/** Points per rule, e.g. "emp:salaried=30, city:metro=20, returned_7d=20, clicked_link=30"; yes at the threshold. */
function score(walk: Walk): string {
  const threshold = Number(text(walk, 'threshold', '70'))
  const hits: string[] = []
  let points = 0
  for (const rule of text(walk, 'rules').split(',')) {
    const [fact = '', worth = '0'] = rule.split('=').map((part) => part.trim())
    if (fact && factHolds(walk, fact)) {
      points += Number(worth) || 0
      hits.push(`${fact} +${worth}`)
    }
  }
  const yes = points >= threshold
  note(
    walk,
    `Hot-lead score ${points} (${hits.join(', ') || 'no rule matched'}) ≥ ${threshold}? → ${yes ? 'yes' : 'no'}`
  )
  return yes ? 'yes' : 'no'
}

function addTag(walk: Walk): string {
  const tag = text(walk, 'tag')
  const group = tag.split(':')[0]
  if (group) {
    walk.lead.tags = [...walk.lead.tags.filter((t) => !t.startsWith(group + ':')), tag]
    walk.out.tags.push(tag)
    note(walk, `Tag ${tag}`)
  }
  return 'next'
}

function putInBatch(walk: Walk): string {
  const batch = walk.state.batches.find((b) => b.id === text(walk, 'batch'))
  if (!batch) {
    note(walk, 'No batch chosen: skipped')
    return 'next'
  }
  const added = addMember(walk.state, batch.id, walk.lead.lead_code)
  walk.out.batches.push(batch.name)
  note(walk, added ? `Put in batch "${batch.name}"` : `Already in batch "${batch.name}"`)
  return 'next'
}

function note(walk: Walk, words: string, node = ''): void {
  walk.out.trace.push({ at: walk.out.at, node, text: words } satisfies TraceStep)
}

const STEP: Partial<Record<NodeType, (walk: Walk) => string>> = {
  'classify.field_check': fieldCheck,
  'classify.list_check': listCheck,
  'classify.score': score,
  'classify.add_tag': addTag,
  'classify.put_in_batch': putInBatch,
}

/**
 * Runs one classification version on one lead. null when the registry's fields API is switched off (the lead stays
 * unclassified; a daily trigger takes it later). `count` = false while the seed is built (no call counted).
 */
export function classifyLead(
  state: State,
  lead: LeadRecord,
  version: VersionRecord,
  count = true
): ClassifyRecord | null {
  if (apiSwitchedOff(state, API_ID.regFields)) return null
  if (count) countCampaignCall(state, [API_ID.regFields], null)
  const document: WorkflowDocument = version.document
  const out: ClassifyRecord = {
    id: newId(state, 'cr'),
    lead_code: lead.lead_code,
    workflow_id: version.workflow_id,
    version: version.version,
    at: nowIso(state),
    tags: [],
    batches: [],
    trace: [],
  }
  const walk: Walk = { state, lead, fields: fieldsOf(state, lead), cfg: {}, out }
  let node = document.nodes.find((n) => n.type === TRIGGER)
  for (let steps = 0; node && steps < MAX_STEPS; steps++) {
    walk.cfg = node.cfg
    let port = 'next'
    if (node.type === TRIGGER) note(walk, `New data: ${lead.lead_code} from ${lead.source}`, node.id)
    else if (node.type === 'classify.end') break
    else {
      const step = STEP[node.type as NodeType]
      if (!step) break
      port = step(walk)
      out.trace[out.trace.length - 1]!.node = node.id
    }
    const edge =
      document.edges.find((e) => e.from === node!.id && e.when === port) ??
      document.edges.find((e) => e.from === node!.id && e.when === 'always')
    node = edge ? document.nodes.find((n) => n.id === edge.to) : undefined
  }
  if (!lead.classified_by.includes(version.workflow_id)) lead.classified_by.push(version.workflow_id)
  state.classifyRuns.unshift(out)
  state.classifyRuns.length = Math.min(state.classifyRuns.length, RUNS_KEPT)
  return out
}

/** The live version of every classification workflow, with its trigger's settings. */
function liveClassifiers(state: State) {
  return state.workflows
    .filter((w) => w.kind === 'classification' && stateOf(w) === 'active')
    .map((w) => liveVersion(state, w.id))
    .filter((v): v is VersionRecord => !!v)
    .map((version) => ({ version, cfg: version.document.nodes.find((n) => n.type === TRIGGER)?.cfg ?? {} }))
}

/** New leads arrived (Fetch now, the partner API): every live workflow triggered on arrival from that source runs. */
export function classifyNew(state: State, leads: LeadRecord[], count = true): void {
  for (const { version, cfg } of liveClassifiers(state)) {
    if ((cfg.when ?? 'arrival') !== 'arrival') continue
    const sources = split(String(cfg.sources ?? 'any'))
    for (const lead of leads) {
      if (sources.includes('any') || sources.includes(lead.source)) classifyLead(state, lead, version, count)
    }
  }
}

/** At a daily trigger's hour: everything that workflow has not classified yet. */
export function classifyDaily(state: State): void {
  for (const { version, cfg } of liveClassifiers(state)) {
    if (cfg.when !== 'daily' || Number(cfg.hour ?? 8) !== state.clock.hour) continue
    for (const lead of state.leads) {
      if (!lead.classified_by.includes(version.workflow_id)) classifyLead(state, lead, version)
    }
  }
}
